import { AppBar, Box, Button, ButtonBase, Container, Toolbar, Typography, Menu, MenuItem, Divider, MenuProps, IconButton } from "@mui/material";
import { Link } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { UserTypeId, useAccountStore } from "../common/account";
import RequireAuth from "./RequireAuth";
import { Person2Rounded, Settings } from "@mui/icons-material";
import { useConfig } from "../common/config";
import { createAxios } from "../common/axios";
import { DropdownMenu } from "../common/DropdownMenu";
import { useDialog } from "../common/dialog";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { MainMenuInactivity } from "./MainMenuInactivity";

const regularUserTypes = [UserTypeId.Admin, UserTypeId.User, UserTypeId.View, UserTypeId.Supervisor, UserTypeId.CollectionAgency];

export default function AuthLayout() {
    const { userTypeId, hasCallCenter } = useAccountStore(p => ({ userTypeId: p.data?.accessTokenDecoded.userTypeId, hasCallCenter: p.data?.accountData.accountInfo.callCenterId != null }));
    const isAdmin = userTypeId === UserTypeId.Admin;
    const isSupervisor = userTypeId === UserTypeId.Supervisor;
    const isTmSupervisor = userTypeId === UserTypeId.TmSupervisor || userTypeId === UserTypeId.Supervisor || userTypeId === UserTypeId.TmManager;
    const isRegularUser = userTypeId != null && regularUserTypes.includes(userTypeId);
    const { landName, isPet } = useConfig(p => ({ landName: p.clientData.landName, isPet: p.clientData.isPet }));

    function button(to: string, label: string) {
        return <Button disableElevation component={Link} to={to}>{label}</Button>;
    }
    function dropdownButton(label: string | JSX.Element) {
        return (open: (event: React.MouseEvent<HTMLElement>) => void) => <Button disableElevation onClick={open}>{label}</Button>;
    }
    function iconDropdownButton(icon: JSX.Element) {
        return (open: (event: React.MouseEvent<HTMLElement>) => void) => <IconButton onClick={open} color="inherit">{icon}</IconButton>;
    }
    function linkMenuItem(close: () => void) {
        return (to: string, label: string) => <MenuItem onClick={close} component={Link} to={to}>{label}</MenuItem>;
    }
    function actionMenuItem(close: () => void) {
        return (label: string, action: () => void) => <MenuItem onClick={() => { close(); action(); }}>{label}</MenuItem>;
    }
    function dropdownMenu(label: string | JSX.Element, menu: (menuProps: MenuProps, itemTemplate: ReturnType<typeof linkMenuItem>) => JSX.Element) {
        return <DropdownMenu triggerElement={dropdownButton(label)} itemTemplate={linkMenuItem} menu={menu} />;
    }
    function iconDropdownMenu(icon: JSX.Element, menu: (menuProps: MenuProps, itemTemplate: ReturnType<typeof linkMenuItem>, close: () => void) => JSX.Element) {
        return <DropdownMenu triggerElement={iconDropdownButton(icon)} itemTemplate={linkMenuItem} menu={menu} />;
    }

    const changePasswordDialog = useDialog(close => <ChangePasswordDialog close={close} />, "xs");

    return <RequireAuth>
        {changePasswordDialog.render()}
        <AppBar position="static" sx={{ p: "4px" }}>
            <Toolbar style={{ paddingLeft: 0, paddingRight: 0 }}>
                <ButtonBase component={Link} to="/" ><Typography variant="h6" p="0 8px">{landName} Aesculap IS</Typography></ButtonBase>
                <Box flexGrow={1} display="flex">
                    {isRegularUser && button("/customers", "Customers")}
                    {isAdmin && button("/articles", "Articles")}
                    {isAdmin && button("/products", "Products")}
                    {isAdmin && button("/inventory-transactions", "Inventory transactions")}
                    {(isAdmin || isTmSupervisor) && button("/users", "Users")}
                    {isAdmin && dropdownMenu("Runs", (menuProps, linkMenuItem) => <Menu {...menuProps}>
                        {linkMenuItem("/runs/mo", "Mail order run")}
                        {linkMenuItem("/runs/starter", "Starter run")}
                        {linkMenuItem("/runs/shipment", "Regular shipment run")}
                        <Divider />
                        {linkMenuItem("/runs/dunning", "Dunning run")}
                        {linkMenuItem("/runs/write-off", "Write-off run")}
                        <Divider />
                        {linkMenuItem("/runs/batch-payments", "Batch payments")}
                        {linkMenuItem("/runs/batch-returns", "Batch returns")}
                        {linkMenuItem("/runs/order-import", "Order import")}
                        {linkMenuItem("/runs/manual-write-off", "Manual W/O import")}
                        {linkMenuItem("/runs/order-cycle-skip-import", "Order cycle skip import")}
                        {linkMenuItem("/runs/customer-note-import", "Customer note import")}
                        {linkMenuItem("/runs/customer-reactivation", "Customer reactivation")}
                        {linkMenuItem("/runs/mo-copy", "Batch MO copy")}
                        {linkMenuItem("/runs/order-delay", "Batch order delay")}
                        {linkMenuItem("/runs/mo-article-replace", "MO article replace")}
                        {linkMenuItem("/runs/email-preferences", "Email preference import")}
                        {isPet && linkMenuItem("/runs/order-deactivation", "Order deactivation")}
                        {linkMenuItem("/runs/gdpr", "GDPR")}
                        {linkMenuItem("/runs/vat-update", "VAT update")}
                        <Divider />
                        {linkMenuItem("/runs/online-return-batch", "Online Return Batch")}
                        <Divider />
                        {linkMenuItem("/runs/history", "Run history")}
                    </Menu>)}
                    {isAdmin && dropdownMenu("Fake runs", (menuProps, linkMenuItem) => <Menu {...menuProps}>
                        {linkMenuItem("/runs/mo/fake", "Mail order run")}
                        {linkMenuItem("/runs/starter/fake", "Starter run")}
                        {linkMenuItem("/runs/shipment/fake", "Regular shipment run")}
                        <Divider />
                        {linkMenuItem("/runs/dunning/fake", "Dunning run")}
                        {linkMenuItem("/runs/write-off/fake", "Write-off run")}
                    </Menu>)}
                    {isAdmin && dropdownMenu("Default Values", (menuProps, linkMenuItem) => <Menu {...menuProps}>
                        {linkMenuItem("/settings/default-values", "General")}
                        {linkMenuItem("/settings/default-values/country", "Country")}
                        {linkMenuItem("/settings/default-values/products", "Products")}
                    </Menu>)}
                    {isAdmin && dropdownMenu("Reports", (menuProps, linkMenuItem) => <Menu {...menuProps}>
                        {linkMenuItem("/reports/payment-errors", "Payment errors")}
                        {linkMenuItem("/reports/rejected-duplicates", "Rejected duplicates")}
                        {linkMenuItem("/reports/overpayments", "Overpayments report")}
                        <Divider />
                        {linkMenuItem("/reports/retention", "Retention report")}
                        {linkMenuItem("/reports/tm-rewards", "TM Rewards")}
                        <Divider />
                        {linkMenuItem("/reports/transaction", "Transaction report")}
                        <Divider />
                        {linkMenuItem("/reports/sales-journal", "Sales journal")}
                        {linkMenuItem("/reports/payment-journal", "Payment journal")}
                        {linkMenuItem("/reports/tm-orders", "TM Orders report")}
                    </Menu>)}
                    {isSupervisor && dropdownMenu("Reports", (menuProps, linkMenuItem) => <Menu {...menuProps}>
                        {linkMenuItem("/reports/retention", "Retention report")}
                    </Menu>)}
                    {hasCallCenter && button("/tm/orders", "TM Orders")}
                    {(isAdmin || isTmSupervisor) && button("/reports/tm-operators", "TM Operators report")}
                </Box>
                <MainMenuInactivity />
                {isAdmin && iconDropdownMenu(<Settings />, (menuProps, linkMenuItem) => <Menu {...menuProps}>
                    {linkMenuItem("/settings/customer-stop-codes", "Customer stop codes")}
                    <Divider />
                    {linkMenuItem("/settings/customer-note-types", "Note types")}
                    {linkMenuItem("/settings/customer-note-action-types", "Note action types")}
                    <Divider />
                    {linkMenuItem("/settings/print-templates", "Print templates")}
                    {linkMenuItem("/settings/mail-order-templates", "Mail order templates")}
                    {linkMenuItem("/settings/special-actions", "Special actions")}
                    {linkMenuItem("/settings/replacement-articles", "Replacement articles")}
                    <Divider />
                    {linkMenuItem("/settings/collection-agencies", "Collection agencies")}
                    {linkMenuItem("/settings/payment-methods", "Payment methods")}
                    {linkMenuItem("/settings/delivery-methods", "Delivery methods")}
                    <Divider />
                    {linkMenuItem("/settings/customer-states", "Customer states")}
                    {linkMenuItem("/settings/order-states", "Order states")}
                    {linkMenuItem("/settings/invoice-states", "Invoice states")}
                    <Divider />
                    {linkMenuItem("/settings/transaction-codes", "Transaction codes")}
                    {linkMenuItem("/settings/warehouses", "Warehouses")}
                    <Divider />
                    {linkMenuItem("/settings/sales-journal", "Sales journal")}
                    {linkMenuItem("/settings/payment-journal", "Payment journal")}
                    <Divider />
                    {linkMenuItem("/settings/call-centers", "Call centers")}
                    {linkMenuItem("/settings/tm-call-results", "TM call results")}
                    <Divider />
                    {linkMenuItem("/settings/resources", "Resources")}
                    {linkMenuItem("/settings/allowed-characters", "Allowed characters")}
                    {linkMenuItem("/system-tasks", "System tasks")}
                    <Divider />
                    {linkMenuItem("/settings/email-preference-content", "Email pref content types")}
                    {linkMenuItem("/settings/email-preference-frequencies", "Email pref frequencies")}
                    {linkMenuItem("/settings/email-preference-types", "Email pref types")}
                </Menu>)}
                {isSupervisor && iconDropdownMenu(<Settings />, (menuProps, linkMenuItem) => <Menu {...menuProps}>
                    {linkMenuItem("/settings/print-templates", "Print templates")}
                </Menu>)}
                {iconDropdownMenu(<Person2Rounded />, (menuProps, _, close) => <Menu {...menuProps}>
                    {actionMenuItem(close)("Change password", () => {
                        changePasswordDialog.show();
                    })}
                    {actionMenuItem(close)("Logout", () => {
                        createAxios(false, false).get("account/logout");
                        useAccountStore.getState().setData(null);
                    })}
                </Menu>)}
            </Toolbar>
        </AppBar>
        <Container maxWidth={false}>
            <Box mt={3} mb={3}>
                <Outlet />
            </Box>
        </Container>
    </RequireAuth>;
}
